import './brokerage-list.scss';

import {
  CheckedState,
  Option,
} from '../../../../presentation/components/table-filter';
import { PaginationSort, useGetEntitiesBrokerages } from '@converge/graphql';
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';

import BrokerageFilter from './brokerage-filter';
import { SortOrder } from '@converge/data';
import {
  CustomizedTable,
  createTextColumn,
  type TableColumn,
} from '../../../../presentation/components/new-customized-table';
import { capitalizeFirstLetterRemoveUnderLine } from '@converge/utils';
import { ConvergeNavigationUrls } from '../../../../contexts';
import { Box } from '@mui/material';

export interface BrokerageFilterOptions {
  parentEntity?: Option[];
  partnerEntity?: Option[];
  brokerage: Option[];
  status: Option[];
  uwRegion?: Option[];
}

interface BrokerageRow {
  id: string;
  brokerage: string;
  parentEntity: string;
  parentEntityUuid?: string;
  partnerEntity?: string;
  partnerEntityUuid?: string;
  uwRegion: string;
  offices: string | number;
  teams: string | number;
  brokers: string | number;
  onboarded: string;
  status: string;
}

const cleanFilters = (filters: CheckedState): { [key: string]: any } => {
  const result: { [key: string]: any } = {};

  Object.keys(filters).forEach((key) => {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) {
      switch (key) {
        case 'brokerage':
          result['entityUuids'] = value;
          break;
        case 'parentEntity':
          result['parentEntityUuids'] = value;
          break;
        case 'partnerEntity':
          result['partnerEntityUuids'] = value;
          break;
        case 'uwRegion':
          result['uwTerritoryUuids'] = value;
          break;
        case 'status':
          result['statuses'] = value;
          break;
        case 'onboardedDate': {
          try {
            const parsed = JSON.parse(value[0]);
            result['createdAt'] = {
              start: parsed[0] ?? null,
              end: parsed[1] ?? null,
            };
          } catch {
            /* ignore malformed */
          }
          break;
        }
        default:
          result[key] = value;
      }
    }
  });

  return result;
};
const defaultFilters = {
  broker: [],
  brokerage: [],
  parentEntity: [],
  partnerEntity: [],
  status: [],
  uwRegion: [],
  onboardedDate: [],
};

const reverseSortFieldMap = {
  UNDERWRITING_TERRITORY_NAME: 'uwRegion',
  PRIMARY_LEGAL_NAME: 'brokerage',
  PARENT_PRIMARY_LEGAL_NAME: 'parentEntity',
  PARTNER_PRIMARY_LEGAL_NAME: 'partnerEntity',
  OFFICE_COUNT: 'offices',
  TEAM_COUNT: 'teams',
  BROKER_COUNT: 'brokers',
  STATUS: 'status',
  CREATED_AT: 'onboarded',
};

const defaultSorting = {
  key: 'CREATED_AT',
  order: SortOrder.Desc,
};

const BrokerageList = ({
  setDefaultBrokerages,
}: {
  setDefaultBrokerages?: (option: Option[]) => void;
}) => {
  const localStoragePageSize: number = parseInt(
    localStorage.getItem('brokeragePageSize') ?? '0'
  );
  const localStoragePageNumber: number = parseInt(
    localStorage.getItem('brokeragePageNumber') ?? '0'
  );
  const localStorageSorting: {
    key: keyof typeof reverseSortFieldMap;
    order: any;
  } = JSON.parse(localStorage.getItem('brokerageSorting') ?? '{}');
  const isLocalStorageSorting = Object.keys(localStorageSorting).length > 0;

  const [currentPage, setCurrentPage] = useState<number>(
    localStoragePageNumber || 0
  );
  const [pageSize, setPageSize] = useState<number>(localStoragePageSize || 10);
  const [currentSort, setCurrentSort] = useState<PaginationSort>(
    isLocalStorageSorting ? localStorageSorting : defaultSorting
  );

  const localStorageBrokerageFilters = localStorage.getItem('brokerageFilters');
  const localStorageBrokerageFilterLabels = localStorage.getItem(
    'brokerageFilterLabels'
  );

  const [brokeragesFilters, setBrokeragesFilters] = useState<CheckedState>(
    JSON.parse(localStorageBrokerageFilters || 'null') || defaultFilters
  );
  const [currentFilters, setCurrentFilters] = useState<CheckedState>(
    JSON.parse(localStorageBrokerageFilters || 'null') || defaultFilters
  );

  const [filterLabels, setFilterLabels] = useState<CheckedState>(
    JSON.parse(localStorageBrokerageFilterLabels || 'null') || defaultFilters
  );

  const [currentFilterLabels, setCurrentFilterLabels] = useState<CheckedState>(
    JSON.parse(localStorageBrokerageFilterLabels || 'null') || defaultFilters
  );

  const brokerageFiltersStorage = JSON.parse(
    localStorageBrokerageFilters || '{}'
  );

  const { brokerages, loading, data } = useGetEntitiesBrokerages({
    pageNumber: currentPage,
    pageSize: pageSize,
    sort: currentSort,
    filters: cleanFilters(brokeragesFilters),
  });
  useEffect(() => {
    if (
      brokerageFiltersStorage &&
      Object.keys(brokerageFiltersStorage)?.length
    ) {
      setCurrentFilters(brokerageFiltersStorage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLinkClick = (value: any, row: BrokerageRow) => {
    window.location.href = ConvergeNavigationUrls.brokerage({
      brokerageUuid: row.id,
    });
  };

  const handleParentEntityClick = (value: any, row: BrokerageRow) => {
    console.log('Parent entity clicked:', {
      value,
      parentEntityUuid: row.parentEntityUuid,
      row,
    });
    if (row.parentEntityUuid) {
      window.location.href = ConvergeNavigationUrls.brokerage({
        brokerageUuid: row.parentEntityUuid,
      });
    }
  };

  const handlePartnerEntityClick = (value: any, row: BrokerageRow) => {
    console.log('Partner entity clicked:', {
      value,
      partnerEntityUuid: row.partnerEntityUuid,
      row,
    });
    if (row.partnerEntityUuid) {
      window.location.href = ConvergeNavigationUrls.brokerage({
        brokerageUuid: row.partnerEntityUuid,
      });
    }
  };

  const columns: TableColumn<BrokerageRow>[] = [
    {
      id: 'brokerage',
      label: 'BROKERAGE',
      flex: '1.8',
      sortable: true,
      headerTestId: 'brokerage-header',
      render: (value, row, rowIndex) => (
        <span
          data-testid={`brokerage_${rowIndex}`}
          style={{
            cursor: 'pointer',
            color: 'black',
            textDecoration: 'underline',
          }}
          onClick={() => handleLinkClick(value, row)}
        >
          {value || '—'}
        </span>
      ),
    },
    createTextColumn<BrokerageRow>('parentEntity', 'PARENT ENTITY', {
      flex: '1.8',
      sortable: true,
      headerTestId: 'parentEntity-header',
      render: (value, row, rowIndex) => (
        <span
          data-testid={`parentEntity_${rowIndex}`}
          style={{
            cursor: value && row.parentEntityUuid ? 'pointer' : 'default',
            color: 'black',
            textDecoration:
              value && row.parentEntityUuid ? 'underline' : 'none',
          }}
          onClick={() =>
            value && row.parentEntityUuid && handleParentEntityClick(value, row)
          }
        >
          {value || '—'}
        </span>
      ),
    }),
    createTextColumn<BrokerageRow>('partnerEntity', 'PARTNER ENTITY', {
      flex: '1.8',
      sortable: true,
      headerTestId: 'partnerEntity-header',
      render: (value, row, rowIndex) => (
        <span
          data-testid={`partnerEntity_${rowIndex}`}
          style={{
            cursor: value && row.partnerEntityUuid ? 'pointer' : 'default',
            color: 'black',
            textDecoration:
              value && row.partnerEntityUuid ? 'underline' : 'none',
          }}
          onClick={() =>
            value &&
            row.partnerEntityUuid &&
            handlePartnerEntityClick(value, row)
          }
        >
          {value || '—'}
        </span>
      ),
    }),
    createTextColumn<BrokerageRow>('uwRegion', 'UW REGION', {
      flex: '1.3',
      sortable: true,
      headerTestId: 'uwRegion-header',
      render: (value, _row, rowIndex) => (
        <span data-testid={`uwRegion_${rowIndex}`}>{String(value) || '—'}</span>
      ),
    }),
    createTextColumn<BrokerageRow>('offices', 'OFFICES', {
      flex: '1.3',
      sortable: true,
      headerTestId: 'offices-header',
      render: (value, _row, rowIndex) => (
        <div data-testid={`offices_${rowIndex}`} style={{ marginLeft: '22px' }}>
          {value || '—'}
        </div>
      ),
    }),
    createTextColumn<BrokerageRow>('teams', 'TEAMS', {
      flex: '1.2',
      sortable: true,
      headerTestId: 'teams-header',
      render: (value, _row, rowIndex) => (
        <div data-testid={`teams_${rowIndex}`} style={{ marginLeft: '20px' }}>
          {value || '—'}
        </div>
      ),
    }),
    createTextColumn<BrokerageRow>('brokers', 'BROKERS', {
      flex: '1.2',
      align: 'left',
      sortable: true,
      headerTestId: 'brokers-header',
      render: (value, _row, rowIndex) => (
        <div data-testid={`brokers_${rowIndex}`} style={{ marginLeft: '24px' }}>
          {value || '—'}
        </div>
      ),
    }),
    createTextColumn<BrokerageRow>('onboarded', 'ONBOARDED', {
      flex: '1.2',
      sortable: true,
      align: 'left',
      headerTestId: 'onboarded-header',
      render: (value, _row, rowIndex) => (
        <span data-testid={`onboarded_${rowIndex}`}>
          {String(value) || '—'}
        </span>
      ),
    }),
    {
      id: 'status',
      label: 'STATUS',
      flex: '1.2',
      align: 'center',
      sortable: true,
      headerTestId: 'status-header',
      render: (value, _row, rowIndex) => {
        const displayValue =
          value && capitalizeFirstLetterRemoveUnderLine(value as string);
        const isActive = displayValue === 'Active';
        const color = isActive ? '#2E7D32' : '#D32F2F';
        const backgroundColor = isActive ? '#EDF7ED' : '#FDEDED';

        return (
          <div>
            <span
              data-testid={`status_${rowIndex}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 12px',
                borderRadius: '16px',
                border: `1px solid ${color}`,
                backgroundColor: `${backgroundColor}`,
                fontSize: '12px',
                fontWeight: '500',
                color: color,
                minWidth: '80px',
                justifyContent: 'center',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {displayValue || '—'}
            </span>
          </div>
        );
      },
    },
  ];

  const handleSortModeChange = (
    columnId: string,
    sortOrder: 'asc' | 'desc' | null
  ) => {
    // Handle reset to default (null) - use CREATED_AT desc as default
    if (sortOrder === null) {
      const defaultSort = { key: 'CREATED_AT', order: SortOrder.Desc };
      localStorage.setItem('brokerageSorting', JSON.stringify(defaultSort));
      setCurrentSort(defaultSort);
      return;
    }

    const sort = { key: currentSort.key, order: SortOrder.Desc };

    switch (columnId) {
      case 'brokerage':
        sort.key = 'PRIMARY_LEGAL_NAME';
        break;
      case 'parentEntity':
        sort.key = 'PARENT_PRIMARY_LEGAL_NAME';
        break;
      case 'partnerEntity':
        sort.key = 'PARTNER_PRIMARY_LEGAL_NAME';
        break;
      case 'uwRegion':
        sort.key = 'UNDERWRITING_TERRITORY_NAME';
        break;
      case 'offices':
        sort.key = 'OFFICE_COUNT';
        break;
      case 'teams':
        sort.key = 'TEAM_COUNT';
        break;
      case 'brokers':
        sort.key = 'BROKER_COUNT';
        break;
      case 'status':
        sort.key = 'STATUS';
        break;
      case 'onboarded':
        sort.key = 'CREATED_AT';
        break;
      default:
        sort.key = 'CREATED_AT';
    }

    switch (sortOrder) {
      case 'asc':
        sort.order = SortOrder.Asc;
        break;
      case 'desc':
        sort.order = SortOrder.Desc;
        break;
      default:
        sort.order = SortOrder.Desc;
    }

    localStorage.setItem('brokerageSorting', JSON.stringify(sort));
    setCurrentSort(sort);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    localStorage.setItem('brokeragePageNumber', newPage.toString());
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    localStorage.setItem('brokeragePageSize', newPageSize.toString());
    localStorage.setItem('brokeragePageNumber', '0');
  };

  const handleResetPageNumber = () => {
    setCurrentPage(0);
    localStorage.setItem('brokeragePageNumber', '0');
  };
  return (
    <Box className="brokerage-list">
      <BrokerageFilter
        setDefaultBrokerages={setDefaultBrokerages}
        currentFilters={currentFilters}
        setCurrentFilters={setCurrentFilters}
        currentFilterLabels={currentFilterLabels}
        setCurrentFilterLabels={setCurrentFilterLabels}
        filterLabels={filterLabels}
        setFilterLabels={setFilterLabels}
        resetPageNumber={handleResetPageNumber}
        setBrokerageFilters={setBrokeragesFilters}
      />

      <CustomizedTable<BrokerageRow>
        title=""
        rowTestIdPrefix="brokerage"
        columns={columns}
        data={brokerages as BrokerageRow[]}
        enableSorting={true}
        enablePagination={true}
        loading={loading}
        maxHeight="calc(80vh - 225px)"
        // Server-side sorting
        onSortChange={(columnId, sortOrder) =>
          handleSortModeChange(columnId as string, sortOrder)
        }
        onSortReset={() => {
          localStorage.setItem(
            'brokerageSorting',
            JSON.stringify(defaultSorting)
          );
          setCurrentSort(defaultSorting);
        }}
        currentSortColumn={
          reverseSortFieldMap[
            currentSort.key as keyof typeof reverseSortFieldMap
          ] as keyof BrokerageRow
        }
        currentSortOrder={currentSort.order === SortOrder.Asc ? 'asc' : 'desc'}
        // Server-side pagination
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        currentPage={currentPage}
        totalItems={data?.entities?.pageInfo?.totalResults}
        defaultRowsPerPage={pageSize}
      />
    </Box>
  );
};

export default BrokerageList;
